import { useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Activity, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { roleLabelTh } from '@/lib/role-label';
import { rolesMatrixQueryOptions, useUpdateRolePermissions } from '@/lib/roles';

export const Route = createFileRoute('/_authenticated/roles/')({
  component: RolesPage,
});

const GROUP_LABELS: Record<string, string> = {
  post: 'บทความ',
  media: 'สื่อ',
  user: 'ผู้ใช้',
  role: 'Role',
  comment: 'ความคิดเห็น',
  category: 'หมวด',
  tag: 'แท็ก',
  settings: 'ตั้งค่า',
  audit: 'Audit',
  analytics: 'Analytics',
};

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function RolesPage() {
  const { user } = useSession();
  const canReadAudit = hasPermission(user, 'audit.read');
  const query = useQuery(rolesMatrixQueryOptions());
  const updateMutation = useUpdateRolePermissions();

  // Draft state per role — initialized from server data, edited locally.
  const [drafts, setDrafts] = useState<Record<string, Set<string>>>({});

  // Original snapshot per role for dirty comparison + reset.
  const originals = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    for (const r of query.data?.roles ?? []) out[r.id] = new Set(r.permissionCodes);
    return out;
  }, [query.data]);

  // Re-sync drafts whenever server data changes (initial load + after Save invalidation).
  useEffect(() => {
    if (!query.data) return;
    const fresh: Record<string, Set<string>> = {};
    for (const r of query.data.roles) fresh[r.id] = new Set(r.permissionCodes);
    setDrafts(fresh);
  }, [query.data]);

  const grouped = useMemo(() => {
    const perms = query.data?.permissions ?? [];
    const map = new Map<string, typeof perms>();
    for (const p of perms) {
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group)!.push(p);
    }
    return Array.from(map.entries());
  }, [query.data]);

  const roles = query.data?.roles ?? [];

  const toggle = (roleId: string, code: string) => {
    setDrafts((prev) => {
      const set = new Set(prev[roleId] ?? []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      return { ...prev, [roleId]: set };
    });
  };

  const isDirty = (roleId: string): boolean => {
    const draft = drafts[roleId];
    const orig = originals[roleId];
    if (!draft || !orig) return false;
    return !setsEqual(draft, orig);
  };

  const handleSave = (roleId: string) => {
    const draft = drafts[roleId];
    if (!draft) return;
    updateMutation.mutate({ roleId, permissionCodes: Array.from(draft) });
  };

  const handleReset = (roleId: string) => {
    setDrafts((prev) => ({ ...prev, [roleId]: new Set(originals[roleId] ?? []) }));
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            สิทธิ์ของแต่ละ Role
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            ติ๊กช่องเพื่อให้/ถอน permission แล้วกด <strong>Save</strong> บนการ์ดของ role
            นั้น&nbsp;ๆ&nbsp;(
            <code className="font-mono text-xs">super_admin</code> ล็อกไว้ — แก้ไม่ได้)
          </p>
        </div>

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : roles.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <ShieldCheck className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ไม่มี role</p>
          </div>
        ) : (
          <>
            {/* Role summary cards with Save/Reset controls */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {roles.map((r) => {
                const dirty = isDirty(r.id);
                const draftSize = drafts[r.id]?.size ?? 0;
                const locked = r.slug === 'super_admin';
                const saving =
                  updateMutation.isPending && updateMutation.variables?.roleId === r.id;
                return (
                  <div
                    key={r.id}
                    className={`rounded-lg border p-3 transition-colors ${
                      dirty
                        ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30'
                        : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
                          {roleLabelTh(r.slug)}
                        </div>
                        <div className="truncate font-mono text-[11px] text-slate-400">
                          {r.slug}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {canReadAudit && (
                          <Link
                            to="/audit"
                            search={{ resourceType: 'role', resourceId: r.id }}
                            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                            title="ดูประวัติการแก้ไข role นี้"
                            aria-label="ดู audit"
                          >
                            <Activity className="h-3.5 w-3.5" />
                          </Link>
                        )}
                        {locked && (
                          <Lock className="h-3.5 w-3.5 text-slate-400" aria-label="ล็อก" />
                        )}
                      </div>
                    </div>
                    <div className="text-brand-600 dark:text-brand-400 mt-2 flex items-baseline gap-1.5 text-lg font-bold">
                      {draftSize}
                      {dirty && (
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
                          aria-label="แก้ไขค้างอยู่"
                        />
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500">สิทธิ์</div>
                    {!locked && dirty && (
                      <div className="mt-2 flex gap-1">
                        <Button
                          size="sm"
                          className="h-7 flex-1 text-[11px]"
                          onClick={() => handleSave(r.id)}
                          disabled={saving}
                        >
                          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => handleReset(r.id)}
                          disabled={saving}
                        >
                          Reset
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Matrix per group */}
            {grouped.map(([group, perms]) => (
              <Card key={group}>
                <CardHeader>
                  <CardTitle className="text-base">
                    {GROUP_LABELS[group] ?? group}{' '}
                    <span className="font-mono text-xs text-slate-400">({group})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-400">
                        <tr>
                          <th className="px-4 py-2 font-medium">Permission</th>
                          {roles.map((r) => (
                            <th
                              key={r.id}
                              className="w-20 px-2 py-2 text-center font-medium"
                              title={r.slug}
                            >
                              {roleLabelTh(r.slug)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                        {perms.map((p) => (
                          <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-4 py-2">
                              <div className="font-mono text-xs text-slate-900 dark:text-slate-100">
                                {p.code}
                              </div>
                              {p.description && (
                                <div className="text-[11px] text-slate-500">{p.description}</div>
                              )}
                            </td>
                            {roles.map((r) => {
                              const checked = drafts[r.id]?.has(p.code) ?? false;
                              const locked = r.slug === 'super_admin';
                              return (
                                <td key={r.id} className="px-2 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(r.id, p.code)}
                                    disabled={locked}
                                    aria-label={`${roleLabelTh(r.slug)} — ${p.code}`}
                                    className="text-brand-600 focus:ring-brand-500 h-4 w-4 cursor-pointer rounded border-slate-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600"
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
